// src/admin/AdminCustomOrders.jsx
import { useEffect, useState } from "react";
import { collection, getDocs, getDoc, updateDoc, doc, query, orderBy } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import API_BASE, { apiRequest, getAuthToken } from "../services/apiClient";
import { downloadAdminPdf } from "./downloadAdminPdf";

const STATUS_OPTIONS = [
  { value: "submitted",     label: "Submitted" },
  { value: "reviewing",     label: "Reviewing" },
  { value: "invoiced",      label: "Invoiced" },
  { value: "in-production", label: "In Production" },
  { value: "completed",     label: "Completed" },
  { value: "cancelled",     label: "Cancelled" },
];

const STATUS_STYLES = {
  submitted:       { bg: "rgba(245,194,0,0.15)",  color: "#f5c200" },
  reviewing:       { bg: "rgba(0,170,221,0.15)",  color: "#00aadd" },
  "in-production":{ bg: "rgba(168,85,247,0.15)", color: "#c084fc" },
  completed:       { bg: "rgba(16,185,129,0.18)", color: "#10d9a0" },
  cancelled:       { bg: "rgba(239,68,68,0.15)",  color: "#f87171" },
};

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

function StatusBadge({ status }) {
  var s = STATUS_STYLES[status] || STATUS_STYLES.submitted;
  var label = (STATUS_OPTIONS.find(function(o) { return o.value === status; }) || STATUS_OPTIONS[0]).label;
  return (
    <span style={{ background: s.bg, color: s.color, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function CustomOrderDetailModal({ order, onClose, onUpdateStatus, updating, onInvoiceSaved }) {
  if (!order) return null;

  var date = order.createdAt?.toDate
    ? order.createdAt.toDate().toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  var shipping = order.shippingAddress;
  var invoice = order.invoice || {};
  var [invoiceForm, setInvoiceForm] = useState({ description: invoice.items?.[0]?.description || order.companyName || "Custom printing", quantity: invoice.quantity || order.qtyRequired || 1, unitPrice: invoice.unitPrice || 0, shipping: invoice.shipping || 0, hstRate: invoice.hstRate ?? 13 });
  var [invoiceBusy, setInvoiceBusy] = useState(false);
  var [invoiceMessage, setInvoiceMessage] = useState("");
  var [fileBusy, setFileBusy] = useState(false);
  var [company, setCompany] = useState({});
  var hstNumber = company.taxId || "805336690 RT0001";
  var [shippingForm, setShippingForm] = useState({ ...(order.shippingAddress || {}) });
  var subtotal = Number(invoiceForm.quantity || 0) * Number(invoiceForm.unitPrice || 0);
  var hst = (subtotal + Number(invoiceForm.shipping || 0)) * Number(invoiceForm.hstRate || 0) / 100;
  var grandTotal = subtotal + Number(invoiceForm.shipping || 0) + hst;
  useEffect(function() { getDoc(doc(db, "siteSettings", "socialDetails")).then(function(snap) { if (snap.exists()) setCompany(snap.data()); }).catch(function() {}); }, []);
  async function saveInvoice(email) {
    setInvoiceBusy(true); setInvoiceMessage("");
    try {
      var saved = await apiRequest(`/admin/custom-orders/${order.id}/invoice`, { method: "POST", body: JSON.stringify({ ...invoiceForm, items: [{ description: invoiceForm.description, quantity: Number(invoiceForm.quantity), unitPrice: Number(invoiceForm.unitPrice) }] }) });
      onInvoiceSaved(saved);
      if (email) {
        var result = await apiRequest(`/admin/custom-orders/${order.id}/invoice/email`, { method: "POST", body: JSON.stringify({}) });
        setInvoiceMessage(result.message);
      } else setInvoiceMessage(`Invoice ${saved.invoice.invoiceNumber} saved.`);
    } catch (error) { setInvoiceMessage(error.message); }
    finally { setInvoiceBusy(false); }
  }
  async function uploadOrderFile(file, field) {
    if (!file) return;
    setFileBusy(true);
    try {
      var formData = new FormData();
      formData.append("file", file);
      var response = await fetch(`${API_BASE}/uploads`, { method: "POST", body: formData });
      var data = await response.json();
      if (!response.ok) throw new Error(data.error || "Upload failed");
      var updated = { ...order, [field]: data.url };
      await updateDoc(doc(db, "customOrders", order.id), { [field]: data.url });
      onInvoiceSaved(updated);
    } catch (error) { setInvoiceMessage(error.message); }
    finally { setFileBusy(false); }
  }
  async function deleteOrderFile(field) {
    if (!order[field] || !window.confirm("Delete this file?")) return;
    setFileBusy(true);
    try {
      await fetch(`${API_BASE}/uploads?url=${encodeURIComponent(order[field])}`, { method: "DELETE", headers: { Authorization: `Bearer ${getAuthToken()}` } });
      var updated = { ...order, [field]: "" };
      await updateDoc(doc(db, "customOrders", order.id), { [field]: "" });
      onInvoiceSaved(updated);
    } catch (error) { setInvoiceMessage(error.message); }
    finally { setFileBusy(false); }
  }
  async function saveShipping() {
    setInvoiceBusy(true); setInvoiceMessage("");
    try {
      await updateDoc(doc(db, "customOrders", order.id), { shippingAddress: shippingForm });
      onInvoiceSaved({ ...order, shippingAddress: shippingForm });
      setInvoiceMessage("Shipping details saved.");
    } catch (error) { setInvoiceMessage(error.message); }
    finally { setInvoiceBusy(false); }
  }

  return (
    <div style={overlayStyle} onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h3 style={{ color: "#fff", fontWeight: 800, fontSize: 20, margin: 0 }}>Custom Order Details</h3>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, marginTop: 4, fontFamily: "monospace" }}>#{order.id}</p>
          </div>
          <button onClick={onClose} style={closeBtnStyle}><CloseIcon /></button>
        </div>

        <div style={statusBoxStyle}>
          <div>
            <p style={labelStyle}>Current Status</p>
            <StatusBadge status={order.status || "submitted"} />
          </div>
          <div style={{ flex: 1, maxWidth: 240 }}>
            <p style={labelStyle}>Update Status</p>
            <select
              value={order.status || "submitted"}
              onChange={function(e) { onUpdateStatus(order.id, e.target.value); }}
              disabled={updating === order.id}
              style={selectStyle(updating === order.id)}
            >
              {STATUS_OPTIONS.map(function(s) {
                return <option key={s.value} value={s.value} style={{ background: "#1a1a2e", color: "#fff" }}>{s.label}</option>;
              })}
            </select>
          </div>
        </div>

        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Customer</p>
          <div style={infoGridStyle}>
            <div><span style={infoLabelStyle}>Company</span><p style={infoValueStyle}>{order.companyName || "—"}</p></div>
            <div><span style={infoLabelStyle}>Email</span><p style={infoValueStyle}>{order.email || "—"}</p></div>
            <div><span style={infoLabelStyle}>Phone</span><p style={infoValueStyle}>{order.phone || "—"}</p></div>
            <div><span style={infoLabelStyle}>Submitted</span><p style={infoValueStyle}>{date}</p></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Order Info</p>
          <div style={infoGridStyle}>
            <div><span style={infoLabelStyle}>Quote Number</span><p style={infoValueStyle}>{order.quoteNumber || "—"}</p></div>
            <div><span style={infoLabelStyle}>Qty Required</span><p style={infoValueStyle}>{order.qtyRequired || "—"}</p></div>
            <div><span style={infoLabelStyle}>PO</span><p style={infoValueStyle}>{order.po || "—"}</p></div>
            <div><span style={infoLabelStyle}>Expected Date</span><p style={infoValueStyle}>{order.expectedDate || "—"}</p></div>
          </div>
          {order.notes && (
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginTop: 12, fontStyle: "italic" }}>📝 {order.notes}</p>
          )}
        </div>

        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Invoice</p>
          <div style={infoGridStyle}>
            <label style={fieldLabelStyle}>Description<input style={invoiceInputStyle} value={invoiceForm.description} onChange={e => setInvoiceForm(f => ({ ...f, description: e.target.value }))} /></label>
            <label style={fieldLabelStyle}>Quantity<input type="number" min="1" style={invoiceInputStyle} value={invoiceForm.quantity} onChange={e => setInvoiceForm(f => ({ ...f, quantity: e.target.value }))} /></label>
            <label style={fieldLabelStyle}>Unit price<input type="number" min="0" step="0.01" style={invoiceInputStyle} value={invoiceForm.unitPrice} onChange={e => setInvoiceForm(f => ({ ...f, unitPrice: e.target.value }))} /></label>
            <label style={fieldLabelStyle}>Shipping<input type="number" min="0" step="0.01" style={invoiceInputStyle} value={invoiceForm.shipping} onChange={e => setInvoiceForm(f => ({ ...f, shipping: e.target.value }))} /></label>
            <label style={fieldLabelStyle}>HST %<input type="number" min="0" step="0.01" style={invoiceInputStyle} value={invoiceForm.hstRate} onChange={e => setInvoiceForm(f => ({ ...f, hstRate: e.target.value }))} /></label>
          </div>
          <p style={{ color: "#fff", lineHeight: 1.7 }}>Shipping: ${Number(invoiceForm.shipping || 0).toFixed(2)}<br />Subtotal: ${(subtotal + Number(invoiceForm.shipping || 0)).toFixed(2)}<br />HST ({Number(invoiceForm.hstRate || 0).toFixed(2)}%): ${hst.toFixed(2)}<br /><strong>Grand Total: ${grandTotal.toFixed(2)} CAD</strong></p>
          {invoice.invoiceNumber && <p style={{ color: "var(--cyan)", fontFamily: "monospace" }}>Invoice: {invoice.invoiceNumber}</p>}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}><button className="adm-btn adm-btn-primary" disabled={invoiceBusy} onClick={() => saveInvoice(false)}>{invoiceBusy ? "Saving..." : "Save invoice"}</button><button className="adm-btn" disabled={invoiceBusy} onClick={() => saveInvoice(true)}>Save &amp; email invoice</button></div>
          {invoice.invoiceNumber && <button className="adm-btn adm-btn-sm" style={{ marginTop: 8 }} onClick={() => downloadAdminPdf("custom-invoice", order.id).catch(error => setInvoiceMessage(error.message))}>Download Invoice PDF</button>}
          {invoiceMessage && <p style={{ color: invoiceMessage.includes("failed") || invoiceMessage.includes("required") ? "#f87171" : "#4ade80", fontSize: 13 }}>{invoiceMessage}</p>}
          <div style={invoicePreviewStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 18, borderBottom: "1px solid #ddd", paddingBottom: 12, marginBottom: 14 }}>
              <div>{(company.logoUrl || "/logo.jpeg") && <img src={company.logoUrl || "/logo.jpeg"} alt={company.name || "Company logo"} style={{ maxWidth: 150, maxHeight: 48, objectFit: "contain", marginBottom: 8 }} />}<br /><strong style={{ fontSize: 17 }}>{company.name || "Circuit Printing Solutions"}</strong><br /><span>{company.address || "Company address"}<br />{company.city || "City"}, {company.state || "Province"} {company.postcode || ""}<br />{company.country || "Canada"}</span></div>
              <div style={{ textAlign: "right" }}><strong>INVOICE</strong><br />{invoice.invoiceNumber || "IN-DDMMYY-001"}<br />HST #: {hstNumber}</div>
            </div>
            <div><strong>Bill To</strong><br />{order.companyName || "Customer"}<br />{order.email || "customer@email.com"}<br />{order.phone || ""}</div>
            <table style={{ width: "100%", marginTop: 14, borderCollapse: "collapse" }}><thead><tr><th style={previewThStyle}>Item</th><th style={previewThStyle}>Qty</th><th style={previewThStyle}>Price</th><th style={{ ...previewThStyle, textAlign: "right" }}>Subtotal</th></tr></thead><tbody><tr><td style={previewTdStyle}>{invoiceForm.description || "Item"}</td><td style={previewTdStyle}>{invoiceForm.quantity || 0}</td><td style={previewTdStyle}>${Number(invoiceForm.unitPrice || 0).toFixed(2)}</td><td style={{ ...previewTdStyle, textAlign: "right" }}>${subtotal.toFixed(2)}</td></tr></tbody></table>
            <div style={{ textAlign: "right", marginTop: 14, lineHeight: 1.7 }}>Shipping &amp; Handling &nbsp; ${Number(invoiceForm.shipping || 0).toFixed(2)}<br />Subtotal &nbsp; ${(subtotal + Number(invoiceForm.shipping || 0)).toFixed(2)}<br />HST ({Number(invoiceForm.hstRate || 0).toFixed(2)}%): ${hst.toFixed(2)}<br /><strong>Grand Total &nbsp; ${grandTotal.toFixed(2)} CAD</strong></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Files</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {order.orderFileUrl ? (
              <div style={fileActionsStyle}><a href={order.orderFileUrl} target="_blank" rel="noopener noreferrer" style={fileLinkStyle}><FileIcon /> Order File</a><button className="adm-btn adm-btn-danger adm-btn-sm" disabled={fileBusy} onClick={() => deleteOrderFile("orderFileUrl")}>Delete</button></div>
            ) : (
              <label style={uploadFileLabelStyle}>Upload order file<input type="file" hidden onChange={e => uploadOrderFile(e.target.files?.[0], "orderFileUrl")} /></label>
            )}
            {order.artworkFileUrl ? (
              <div style={fileActionsStyle}><a href={order.artworkFileUrl} target="_blank" rel="noopener noreferrer" style={fileLinkStyle}><FileIcon /> Artwork File</a><button className="adm-btn adm-btn-danger adm-btn-sm" disabled={fileBusy} onClick={() => deleteOrderFile("artworkFileUrl")}>Delete</button></div>
            ) : (
              <label style={uploadFileLabelStyle}>Upload artwork file<input type="file" hidden onChange={e => uploadOrderFile(e.target.files?.[0], "artworkFileUrl")} /></label>
            )}
          </div>
        </div>

        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Shipping</p>
          {order.shippingOption === "pickup" ? (
            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 14 }}>Customer will pick up at the facility.</p>
          ) : shipping ? (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={shippingGridStyle}>{[["contactName", "Contact name"], ["address1", "Address"], ["address2", "Address 2"], ["city", "City"], ["province", "Province"], ["postalCode", "Postal code"], ["country", "Country"]].map(function(pair) { return <label key={pair[0]} style={fieldLabelStyle}>{pair[1]}<input style={invoiceInputStyle} value={shippingForm[pair[0]] || ""} onChange={function(e) { setShippingForm(function(prev) { return { ...prev, [pair[0]]: e.target.value }; }); }} /></label>; })}</div>
              <button className="adm-btn adm-btn-primary" disabled={invoiceBusy} onClick={saveShipping}>{invoiceBusy ? "Saving..." : "Save shipping details"}</button>
            </div>
          ) : (
            <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>—</p>
          )}
        </div>

      </div>
    </div>
  );
}

export default function AdminCustomOrders() {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter]     = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  async function fetchOrders() {
    try {
      var q = query(collection(db, "customOrders"), orderBy("createdAt", "desc"));
      var snap = await getDocs(q);
      setOrders(snap.docs.map(function(d) { return { id: d.id, ...d.data() }; }));
    } catch {
      var snap2 = await getDocs(collection(db, "customOrders"));
      setOrders(snap2.docs.map(function(d) { return { id: d.id, ...d.data() }; }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(function() { fetchOrders(); }, []);

  async function updateStatus(id, status) {
    setUpdating(id);
    try {
      var updated = await apiRequest(`/admin/custom-orders/${id}/status`, { method: "PATCH", body: JSON.stringify({ status: status }) });
      setOrders(function(prev) {
        return prev.map(function(o) { return o.id === id ? updated : o; });
      });
      setSelectedOrder(function(prev) { return prev && prev.id === id ? updated : prev; });
    } catch {
      alert("Failed to update status. Please try again.");
    } finally {
      setUpdating(null);
    }
  }

  var filteredOrders = filter === "all" ? orders : orders.filter(function(o) { return (o.status || "submitted") === filter; });

  var counts = {};
  STATUS_OPTIONS.forEach(function(s) { counts[s.value] = orders.filter(function(o) { return (o.status || "submitted") === s.value; }).length; });

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>Loading custom orders...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={function() { setFilter("all"); }} style={filterBtnStyle(filter === "all")}>
          All ({orders.length})
        </button>
        {STATUS_OPTIONS.map(function(s) {
          return (
            <button key={s.value} onClick={function() { setFilter(s.value); }} style={filterBtnStyle(filter === s.value)}>
              {s.label} ({counts[s.value] || 0})
            </button>
          );
        })}
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">Custom Orders ({filteredOrders.length})</span>
        </div>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Company / Contact</th>
                <th>Quote #</th>
                <th>Invoice / Order #</th>
                <th>Qty</th>
                <th>Expected Date</th>
                <th>Files</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center", color: "rgba(255,255,255,0.3)", padding: 40 }}>No custom orders found.</td></tr>
              )}
              {filteredOrders.map(function(o) {
                return (
                  <tr key={o.id} onClick={function() { setSelectedOrder(o); }} style={{ cursor: "pointer" }}>
                    <td>
                      <div style={{ color: "white", fontWeight: 600 }}>{o.companyName}</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{o.email}</div>
                    </td>
                    <td style={{ color: "rgba(255,255,255,0.8)" }}>{o.quoteNumber || "—"}</td>
                    <td style={{ color: o.invoice?.invoiceNumber ? "var(--cyan)" : "rgba(255,255,255,0.65)", fontFamily: "monospace", fontSize: 12 }}>{o.invoice?.invoiceNumber || o.id || "—"}</td>
                    <td style={{ color: "var(--cyan)", fontWeight: 700 }}>{o.qtyRequired || "—"}</td>
                    <td style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{o.expectedDate || "—"}</td>
                    <td style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                      {[o.orderFileUrl && "Order", o.artworkFileUrl && "Artwork"].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td><StatusBadge status={o.status || "submitted"} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedOrder && (
        <CustomOrderDetailModal
          order={selectedOrder}
          onClose={function() { setSelectedOrder(null); }}
          onUpdateStatus={updateStatus}
          updating={updating}
          onInvoiceSaved={function(updated) { setOrders(function(prev) { return prev.map(function(o) { return o.id === updated.id ? updated : o; }); }); setSelectedOrder(updated); }}
        />
      )}

      <style>{`
        .adm-table tbody tr:hover { background: rgba(255,255,255,0.03); }
      `}</style>
    </div>
  );
}

function filterBtnStyle(active) {
  return {
    background: active ? "var(--cyan)" : "rgba(255,255,255,0.06)",
    color: active ? "#fff" : "rgba(255,255,255,0.6)",
    border: "1px solid " + (active ? "var(--cyan)" : "rgba(255,255,255,0.12)"),
    padding: "7px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "var(--font-main)",
    transition: "all 0.15s",
  };
}

var overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.7)",
  backdropFilter: "blur(4px)",
  zIndex: 9999,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};

var modalStyle = {
  background: "#1a1a2e",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 16,
  padding: 28,
  width: "100%",
  maxWidth: 620,
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
};

var closeBtnStyle = {
  background: "rgba(255,255,255,0.08)",
  border: "none",
  color: "rgba(255,255,255,0.6)",
  width: 34, height: 34,
  borderRadius: 8,
  cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0,
};

var statusBoxStyle = {
  display: "flex",
  alignItems: "flex-end",
  gap: 24,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10,
  padding: 16,
  marginBottom: 20,
};

var labelStyle = { color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 };

function selectStyle(disabled) {
  return {
    width: "100%",
    background: "#1a1a2e",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 6,
    padding: "8px 12px",
    color: "white",
    fontFamily: "var(--font-main)",
    fontSize: 13,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
}

var sectionStyle = { marginBottom: 22 };
var sectionTitleStyle = { color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 };
var infoGridStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };
var infoLabelStyle = { color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 600 };
var infoValueStyle = { color: "#fff", fontSize: 14, marginTop: 2 };
var fieldLabelStyle = { display: "flex", flexDirection: "column", gap: 6, color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 700 };
var invoiceInputStyle = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: "8px 10px", color: "#fff", fontFamily: "inherit", fontSize: 13 };
var shippingGridStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
var invoicePreviewStyle = { marginTop: 18, background: "#fff", color: "#111", borderRadius: 8, padding: 18, fontSize: 12, lineHeight: 1.45, boxShadow: "0 8px 24px rgba(0,0,0,0.18)" };
var previewThStyle = { textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px 4px", fontSize: 11 };
var previewTdStyle = { borderBottom: "1px solid #eee", padding: "8px 4px" };

var fileLinkStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: "var(--cyan)",
  fontSize: 13,
  fontWeight: 600,
  textDecoration: "none",
  background: "rgba(0,170,221,0.08)",
  border: "1px solid rgba(0,170,221,0.2)",
  borderRadius: 8,
  padding: "8px 12px",
  width: "fit-content",
};
var fileActionsStyle = { display: "flex", alignItems: "center", gap: 8 };
var uploadFileLabelStyle = { color: "var(--cyan)", fontSize: 13, fontWeight: 600, cursor: "pointer", border: "1px dashed rgba(0,170,221,0.4)", borderRadius: 8, padding: "8px 12px", width: "fit-content" };
